import { set } from 'date-fns';

/**
 * Date format that is expected by "AWSDateTime" graphQL type
 * @param dateValue
 * @example awsDateTime()
 * // returns '2022-05-30T23:09:39.679Z'
 */
export const awsDateTime = (dateValue?: Date | number | string): string => {
  if (dateValue) {
    return new Date(dateValue).toISOString();
  } else {
    return new Date().toISOString();
  }
};

/**
 * Parse a time string into a Date object. Useful for comparing times.
 * @param time in ISO time format, eg 'T16:00'
 */
export const parseTime = (time: string): Date => {
  const [hours, minutes] = time.slice(1).split(':').map(Number);
  return set(new Date(), { hours, milliseconds: 0, minutes, seconds: 0 });
};

/**
 * Calculate the elapsed time between two timestamps and format it as a string, eg `+ 01:03.234`.
 * @param start - The start timestamp in milliseconds.
 * @param end - The end timestamp in milliseconds.
 * @param showMs - Whether to include milliseconds in the output. Defaults to true.
 * @returns A formatted string representing the elapsed time in the format "+MM:SS.mmm" or "-MM:SS.mmm".
 */
export const elapsedTime = (
  { start, end }: { start: number | null | undefined; end: number | null | undefined },
  { showMs }: { showMs: boolean } = { showMs: true },
): string => {
  if (!start || !end) {
    return '-';
  }

  if (end < start) {
    return '(invalid)';
  }

  const diffMs = end - start;

  // Work with absolute value to avoid issues with negative modulo
  const absDiffMs = Math.abs(diffMs);

  // Extract total minutes, seconds, and milliseconds
  const totalMinutes = Math.floor(absDiffMs / (1000 * 60));
  const seconds = Math.floor((absDiffMs % (1000 * 60)) / 1000);
  // Ensure milliseconds are truncated to integer (0-999)
  const milliseconds = Math.floor(absDiffMs % 1000);

  // Pad with zeros as needed
  const minutesStr = totalMinutes.toString().padStart(2, '0');
  const secondsStr = seconds.toString().padStart(2, '0');
  const millisecondsStr = milliseconds.toString().padStart(3, '0');

  if (!showMs) {
    return `${minutesStr}:${secondsStr}`;
  }
  return `${minutesStr}:${secondsStr}.${millisecondsStr}`;
};
